import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { marketingApiService } from '../../marketing-v2/services/marketingApiService';
import { marketingAction } from '../../../_actions/marketing.actions';
import SlidePanel from '../../shared/SlidePanel';
import InfoSheet from '../../shared/InfoSheet';
import { buildSampleEmails, buildSampleSms, SampleDataBanner, SampleDataPrompt } from './sampleCampaigns';
import CampaignsHelp from './CampaignsHelp';
import SmsWalletTopupModal from '../../marketing-v2/features/sms-composer/SmsWalletTopupModal';
import { formatPence, formatUnitPence, walletBalancePence, messagePrices, creditsToPence } from '../../pricing/money';
import { BlockRenderer } from '../../marketing-v2/features/email-composer/components';
import { EmailBlocksPreview } from '../../marketing-v2/features/email-composer/components/EmailHtml';
import { themeSpecFromBlocks, resolveTheme } from '../../marketing-v2/features/email-composer/email/theme';
import useSites from '../../multiLocation/useSites';
import BrandNudge from '../../marketing-v2/features/brand/BrandNudge';
import { EMAIL_TEMPLATES_V2_VISIBLE, CAMPAIGNS_VISIBLE } from '../../../config/featureFlags';
import CampaignsList from '../../marketing-v2/features/campaigns/components/CampaignsList';
import { listCampaigns, campaignOf, subscribeCampaigns, hydrateCampaigns } from '../../marketing-v2/features/campaigns/data/campaignStore';
import CampaignBroadcastModal from '../../multiLocation/CampaignBroadcastModal';
import { useViewingSites } from '../../multiLocation/viewingSites';
import { SalonRunsMarketingBanner, isHeld } from '../SalonRunsMarketing';
import '../../shared/ui.css';
import './Campaigns.css';

/* ---------- Icons (18px stroke, matches catalogue language) ---------- */
const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
  </svg>
);
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
const SearchIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" />
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
);
const MegaphoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m3 11 15-6v14l-15-6v-2Z" /><path d="M11.6 16.8a3 3 0 1 1-5.2-3" /><path d="M18 8a4 4 0 0 1 0 8" />
  </svg>
);
// Legacy (flag off) branch only, where the number really is SMS remaining.
const AllowanceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);
// Credits power any channel, so a message bubble was too SMS-specific. A
// spark reads as "what your sends run on", and matches the top-up modal.
const CreditIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13 2 3 14h7l-1 8 11-14h-7l1-6z" />
  </svg>
);

/* ---------- Helpers ---------- */
const SOFT_GRADIENTS = [
  'linear-gradient(135deg, #29282D 0%, #4a4954 100%)',
  'linear-gradient(135deg, #007EA0 0%, #00acc9 100%)',
  'linear-gradient(135deg, #b08968 0%, #d6a77a 100%)',
  'linear-gradient(135deg, #6d597a 0%, #9a8aa8 100%)',
  'linear-gradient(135deg, #355c4f 0%, #5a8a78 100%)',
  'linear-gradient(135deg, #b5838d 0%, #d9aab2 100%)',
];
const gradientFor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return SOFT_GRADIENTS[h % SOFT_GRADIENTS.length];
};
const initialsFor = (str = '') => {
  const words = str.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '✦';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/* ---- Campaign thumbnail -------------------------------------------------
   A 44px abstraction of the actual email rather than initials on a random
   gradient: the campaign's own palette, and one band per block in the order
   they appear. Two designs that look different in the inbox look different
   here, which initials could never do.

   emailBlocks rides along on the list payload, so this needs no extra fetch.
   Anything without blocks (SMS-era rows, drafts saved before the builder)
   falls back to the old initials treatment.                                */
const BLOCK_BANDS = {
  'layout-utility-bar': { h: 2 },
  'layout-editorial-hero': { h: 9, kind: 'head' },
  header: { h: 9, kind: 'head' },
  'layout-shape-image': { h: 11, kind: 'image' },
  image: { h: 11, kind: 'image' },
  'layout-section-intro': { h: 4, kind: 'head' },
  'layout-stats': { h: 4, kind: 'chips' },
  'layout-quote-chips': { h: 4, kind: 'chips' },
  service: { h: 5, kind: 'card' },
  product: { h: 5, kind: 'card' },
  voucher: { h: 5, kind: 'card' },
  text: { h: 4 },
  'layout-cta-gradient': { h: 6, kind: 'cta' },
  button: { h: 6, kind: 'cta' },
  footer: { h: 3, kind: 'footer' },
  // Email-native sections (the new templates)
  's-banner': { h: 2 },
  's-header': { h: 4, kind: 'head' },
  's-hero': { h: 11, kind: 'image' },
  's-text': { h: 4 },
  's-button': { h: 5, kind: 'cta' },
  's-image': { h: 10, kind: 'image' },
  's-gallery': { h: 8, kind: 'image' },
  's-split': { h: 8, kind: 'image' },
  's-feature': { h: 8, kind: 'card' },
  's-wordmark': { h: 5, kind: 'head' },
  's-cards': { h: 6, kind: 'card' },
  's-offer': { h: 6, kind: 'card' },
  's-menu': { h: 5, kind: 'card' },
  's-voucher': { h: 6, kind: 'card' },
  's-quote': { h: 4, kind: 'chips' },
  's-team': { h: 6, kind: 'image' },
  's-steps': { h: 4, kind: 'chips' },
  's-stats': { h: 4, kind: 'chips' },
  's-info': { h: 4 },
  's-signoff': { h: 3 },
};

const firstColour = (blocks, keys) => {
  for (const b of blocks) {
    const c = b && b.content;
    if (!c) continue;
    for (const k of keys) {
      const v = c[k];
      if (typeof v === 'string' && /^#[0-9a-f]{3,8}$/i.test(v)) return v;
    }
  }
  return null;
};

const CampaignThumb = ({ campaign }) => {
  const blocks = (campaign._raw && campaign._raw.emailBlocks) || campaign.emailBlocks;
  if (!Array.isArray(blocks) || !blocks.length) {
    return <div className="cmp-thumb" style={{ background: gradientFor(campaign.name) }}>{initialsFor(campaign.name)}</div>;
  }
  // New-style emails carry their palette on the footer's theme
  const spec = themeSpecFromBlocks(blocks);
  const palette = spec ? resolveTheme(spec).palette : null;
  const paper = (palette && palette.paper) || firstColour(blocks, ['backgroundColor']) || '#FFFFFF';
  const inkColour = (palette && palette.ink) || firstColour(blocks, ['textColor', 'priceColor']) || '#29282D';
  const accent = (palette && palette.accent) || firstColour(blocks, ['buttonColor', 'accentColor', 'chipColor', 'frameColor']) || inkColour;

  // Fill the tile by height budget rather than a fixed count: block mixes
  // vary wildly, and a count that fits a text-heavy email overflows an
  // image-heavy one. 44px tile minus 3px padding each side, minus gaps.
  const BUDGET = 38;
  const bands = [];
  let used = 0;
  for (const b of blocks) {
    const band = BLOCK_BANDS[b && b.type];
    if (!band) continue;
    const cost = band.h + (bands.length ? 1.5 : 0);
    if (used + cost > BUDGET) break;
    used += cost;
    bands.push(band);
  }

  return (
    <div className="cmp-thumb cmp-thumb--mini" style={{ background: paper }} aria-hidden>
      {bands.map((band, i) => {
        const key = `${i}-${band.kind || 'bar'}`;
        if (band.kind === 'image') {
          return <span key={key} className="cmp-mini-img" style={{ height: band.h, background: accent, opacity: 0.5 }} />;
        }
        if (band.kind === 'cta') {
          return <span key={key} className="cmp-mini-cta" style={{ height: band.h, background: accent }} />;
        }
        if (band.kind === 'chips') {
          return (
            <span key={key} className="cmp-mini-chips" style={{ height: band.h }}>
              {[0, 1, 2].map((n) => <i key={n} style={{ background: accent, opacity: 0.55 }} />)}
            </span>
          );
        }
        if (band.kind === 'card') {
          return <span key={key} className="cmp-mini-card" style={{ height: band.h, borderColor: inkColour }} />;
        }
        return (
          <span
            key={key}
            className="cmp-mini-bar"
            style={{
              height: band.h,
              background: inkColour,
              opacity: band.kind === 'head' ? 0.85 : band.kind === 'footer' ? 0.3 : 0.45,
              width: band.kind === 'head' ? '78%' : '100%',
            }}
          />
        );
      })}
    </div>
  );
};
const formatDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};
const pct = (num, den) => (num && den ? Math.round((num / den) * 100) : null);

// Human label for an audienceSegment id ('vip', 'cat:colour', …) — shown in the
// detail panes so marketers can see who a campaign went to.
// Exported so the assistant's draft card names an audience the same way this
// page does. Two names for one segment is a difference the reader has to
// resolve, and they cannot.
export const segmentLabel = (id) => {
  if (!id) return null;
  const fixed = {
    all: 'All clients',
    vip: 'VIP clients',
    inactive: 'Inactive clients',
    new: 'New clients',
    overdue: 'Overdue regulars',
    custom: 'Hand-picked clients',
  };
  if (fixed[id]) return fixed[id];
  if (id.startsWith('cat:')) {
    return 'Booked ' + id.slice(4).replace(/\b\w/g, (ch) => ch.toUpperCase());
  }
  return id;
};

// A draft Bailey made in the chat (source 'bailey:chat:…'). Plan-made ones
// carry 'bailey:<plan>:<channel>' and sit inside their campaign instead.
export const isBaileyDraft = (c) => !!c && /^bailey:chat:/.test(String(c.source || (c._raw && c._raw.source) || ''));
const fromBailey = (c) => (isBaileyDraft(c) ? <span className="cmp-part-of">From Bailey</span> : null);

// Opening one by id (Bailey's "Open in Campaigns", the rail): a draft goes
// straight into its editor, anything else to its details. Nothing opens to
// edit while the salon runs this stylist's marketing.
export const opensInEditor = (c, managed = false) => !managed && !!c && c.status === 'draft';

// Why a recipient was skipped at send time (BE skipReason → salon-facing label)
const skipLabel = (reason) => (
  reason === 'opted_out' ? 'Opted out of marketing'
    : reason === 'suppressed' ? 'Unsubscribed'
      : reason === 'not_reachable' ? "Couldn't be reached"
        : reason === 'cancelled' ? 'Stopped before it reached them'
        : reason === 'duplicate_address' ? 'Same address as another client (sent once)'
        : reason === 'sent_from_their_usual_location' || reason === 'already_sent_by_another_location' ? 'Sent from another of your locations'
          : 'Skipped before sending'
);

const STATUS_TABS = [
  { id: 'all', label: 'All' },
  { id: 'sent', label: 'Sent' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'draft', label: 'Drafts' },
];

const StatusPill = ({ status, held, heldReason }) => {
  // Held at send time because the credits ran out (the server put it back to
  // draft rather than send it unpaid). Top up, then send it again.
  // Held because it was too long past its time when the send job reached it
  if (heldReason === 'credit_check_failed' && status === 'draft') return <span className="cmp-pill cmp-pill--draft" title="Not sent: Beu couldn’t check your balance when it came to go. Send it again.">Not sent</span>;
  if (heldReason === 'missed_send_time' && status === 'draft') return <span className="cmp-pill cmp-pill--draft" title="Not sent: it was more than two hours past its time when it came to go. Pick a new time to send it.">Missed its time</span>;
  if (heldReason === 'insufficient_credits' && status === 'draft') return <span className="cmp-pill cmp-pill--draft" title="Not sent: your message balance was too low when it was due. Top up, then send it again.">Needs a top-up</span>;
  // Held: salon-controlled marketing is on, so the server will not send it.
  if (held && status !== 'sent') return <span className="cmp-pill cmp-pill--draft" title="Not sent. The salon runs your marketing.">On hold</span>;
  if (status === 'sent') return <span className="cmp-pill cmp-pill--sent"><CheckIcon />Sent</span>;
  if (status === 'scheduled') return <span className="cmp-pill cmp-pill--scheduled"><ClockIcon />Scheduled</span>;
  // Going out now (this used to fall through to "Draft")
  if (status === 'sending') return <span className="cmp-pill cmp-pill--scheduled"><ClockIcon />Sending</span>;
  // Stopped part way by the salon: some had gone, nobody else will get it
  if (status === 'cancelled') return <span className="cmp-pill cmp-pill--draft" title="Stopped part way. Clients it had already reached still got it.">Stopped</span>;
  if (status === 'failed') return <span className="cmp-pill cmp-pill--draft" title="It couldn’t be sent.">Failed</span>;
  if (status === 'skipped') return <span className="cmp-pill cmp-pill--draft" title="Not sent: the times it offered were taken by then.">Called off</span>;
  return <span className="cmp-pill cmp-pill--draft">Draft</span>;
};

const SkeletonList = () => (
  <div className="cmp-list" aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <div className="cmp-skel-row" key={i}>
        <div className="cmp-skel-main">
          <div className="cmp-skel cmp-skel--thumb" />
          <div className="cmp-skel-lines">
            <div className="cmp-skel" style={{ height: 12, width: '55%' }} />
            <div className="cmp-skel" style={{ height: 10, width: '35%' }} />
          </div>
        </div>
        <div className="cmp-skel" style={{ height: 20, width: 78, justifySelf: 'center' }} />
        <div className="cmp-skel" style={{ height: 12, width: 64, justifySelf: 'end' }} />
        <div className="cmp-skel" style={{ height: 12, width: 36, justifySelf: 'end' }} />
        <div className="cmp-skel" style={{ height: 12, width: 36, justifySelf: 'end' }} />
      </div>
    ))}
  </div>
);

const useIsMobile = () => {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const on = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return m;
};

function CampaignsHubComponent({ dispatch, onCreateEmail, onCreateSMS, onOpenCampaign, onOpenSMS, openCampaignId, onOpenConsumed, openGroupId = null, newCampaign = null, showCampaigns = null, managedBySalon = false, managedBySalonName = '' }) {
  // Salon-controlled marketing: the salon runs this stylist's marketing. The
  // list stays readable (their history is theirs), but nothing here creates,
  // edits, sends or buys. The server refuses all of those regardless.
  const managed = !!managedBySalon;
  const { sites, active, activeSalonId, multi } = useSites();
  const PAGE_LIMIT = 20;
  // Campaigns (posts, Stories, texts and emails together) sit beside Email
  // and SMS, which are exactly as they were. The tab opens on Email until
  // the salon has a campaign.
  const [channel, setChannel] = useState(() => (CAMPAIGNS_VISIBLE && !openCampaignId ? 'campaigns' : 'email'));
  const [groups, setGroups] = useState(() => (CAMPAIGNS_VISIBLE ? listCampaigns() : []));
  useEffect(() => (CAMPAIGNS_VISIBLE ? subscribeCampaigns(() => setGroups(listCampaigns())) : undefined), []);
  useEffect(() => { if (CAMPAIGNS_VISIBLE) hydrateCampaigns(); }, []);
  // "Part of Halloween": an email or text that belongs to a campaign
  const partOf = (c) => {
    const g = CAMPAIGNS_VISIBLE && c && c.id
      ? (campaignOf(c.id, groups) || (c.groupId ? groups.find(x => x.id === c.groupId && x.status !== 'cancelled') || null : null))
      : null;
    return g ? <span className="cmp-part-of">Part of {g.name}</span> : null;
  };
  useEffect(() => { if (CAMPAIGNS_VISIBLE && (newCampaign || openGroupId || showCampaigns)) setChannel('campaigns'); }, [newCampaign, openGroupId, showCampaigns]);
  // An email or text opened from a campaign
  const [openId, setOpenId] = useState(openCampaignId || null);
  useEffect(() => { if (openCampaignId) setOpenId(openCampaignId); }, [openCampaignId]);
  // Which locations the list covers: the header Location picker's selection.
  // Undefined means "the site this device is on", which is what a single-site
  // salon always gets, so nothing changes for them.
  const viewing = useViewingSites();
  const salonIds = viewing.spans ? viewing.salonIds : undefined;
  const [status, setStatus] = useState('all');
  const [query, setQuery] = useState('');
  // Server-paged list (Product.js pattern): rows append 20 at a time on
  // scroll; tabs/search/status all filter server-side.
  const [rows, setRows] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [serverTotals, setServerTotals] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fetchTokenRef = useRef(0);
  // True only until the very first page lands. Later tab/search switches
  // still set `loading`, but keep the last page on screen (dimmed) instead
  // of collapsing to the 5-row skeleton and hiding the KPI row every time —
  // that swap was the jank (list height + KPI block both jumping on every click).
  const hasLoadedOnceRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [remainingSms, setRemainingSms] = useState(null);
  const [walletCredits, setWalletCredits] = useState(null);
  const [emailSpendable, setEmailSpendable] = useState(null);
  // Reminders/confirmations run on their own free SMS pool and never draw
  // from credits. Shown so the two are visibly separate rather than implied.
  const [automatedSms, setAutomatedSms] = useState(null);
  // Unified free pool: ONE allowance in credits. When the BE runs it, the
  // per-channel "remaining" figures are two VIEWS of this same number (160
  // credits = 160 emails or 53 texts), so they must never be added together.
  const [freeCredits, setFreeCredits] = useState(null);
  const [freeUsage, setFreeUsage] = useState(null);
  const [unifiedFree, setUnifiedFree] = useState(false);
  const [creditCosts, setCreditCosts] = useState(null);
  // The same balance in pence (balancePence when the API sends it, else
  // credits × 2p), and what a text / email costs: people see pounds only.
  const [walletPence, setWalletPence] = useState(null);
  const [freePence, setFreePence] = useState(null);
  const [unitPrices, setUnitPrices] = useState(null);
  const [autoRecharge, setAutoRecharge] = useState(null);
  const [poolAware, setPoolAware] = useState(false);
  // Default true so an owner (and any BE that predates these fields) is unaffected.
  const [canTopUp, setCanTopUp] = useState(true);
  const [salonCoversMarketing, setSalonCoversMarketing] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const infoRef = useRef(null);
  const [showTopup, setShowTopup] = useState(false);
  const [detail, setDetail] = useState(null);
  // Explicit, clearly-labelled sample-data preview for exploring analytics
  // before any real campaign has been sent. Local only.
  const [demoMode, setDemoMode] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [broadcastCampaign, setBroadcastCampaign] = useState(null);
  const isMobile = useIsMobile();
  // Only the very first fetch shows the skeleton/hides the KPI row. Later
  // tab/search switches keep showing the last page (dimmed via `loading`
  // below) instead of collapsing the layout on every click.
  const isInitialLoad = loading && !hasLoadedOnceRef.current;

  // Centralised paged fetcher with append support and a stale-response token
  // (mirrors Product.js). BE handles type/status/search; FE just pages.
  const fetchPage = async (opts = {}) => {
    const p = opts.page ?? 1;
    const append = !!opts.append;
    const myToken = ++fetchTokenRef.current;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await marketingApiService.getCampaignsPaged(dispatch, {
        type: opts.channel ?? channel,
        currentPage: p,
        limit: PAGE_LIMIT,
        ...((opts.status ?? status) !== 'all' ? { status: opts.status ?? status } : {}),
        ...((opts.search ?? query) ? { search: opts.search ?? query } : {}),
        // Sent only when the owner has actually picked locations. The server
        // validates the list against their own access and ignores it
        // otherwise, so this is a request, never a grant.
        ...((opts.salonIds ?? salonIds) ? { salonIds: opts.salonIds ?? salonIds } : {}),
      });
      if (myToken !== fetchTokenRef.current) return; // stale
      setRows((prev) => (append ? [...prev, ...res.campaigns] : res.campaigns));
      setStatusCounts(res.statusCounts || {});
      if (res.totals) setServerTotals(res.totals);
      setPage(p);
      setHasMore(res.hasMore);
    } catch (e) {
      if (myToken === fetchTokenRef.current && !append) setRows([]);
    } finally {
      if (myToken === fetchTokenRef.current) {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const loadCampaigns = () => fetchPage({ page: 1 });

  // One effect drives every filter change: channel/status refetch immediately,
  // typing debounces 300ms. The token guard handles overlapping responses.
  useEffect(() => {
    if (demoMode) return undefined;
    if (channel === 'campaigns') { setLoading(false); return undefined; }
    const t = setTimeout(() => { fetchPage({ page: 1 }); }, query ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, status, query, salonIds, demoMode, dispatch]);

  // Infinite scroll — load the next 20 when near the bottom of the window
  useEffect(() => {
    const onScroll = () => {
      if (demoMode || loading || loadingMore || !hasMore || detail) return;
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 400) {
        fetchPage({ page: page + 1, append: true });
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, hasMore, loading, loadingMore, demoMode, detail, channel, status, query]);

  useEffect(() => {
    let alive = true;
    loadSmsBalance(() => alive);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Coming back from hosted checkout. The webhook does the crediting, so the
  // balance may land a beat after the redirect — re-read shortly after, and drop
  // the query param so a refresh doesn't re-announce it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topup = params.get('topup');
    if (!topup) return undefined;
    if (topup === 'success') {
      loadSmsBalance();
      const t = setTimeout(() => loadSmsBalance(), 2500);
      window.history.replaceState({}, '', window.location.pathname);
      return () => clearTimeout(t);
    }
    window.history.replaceState({}, '', window.location.pathname);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Balance breakdown popover: dismiss on outside click or Escape.
  // Web only: the anchored popover lives inside infoRef, so an outside click
  // dismisses it. On mobile the breakdown is an InfoSheet portalled to body —
  // every tap inside it is "outside" this ref, and it brings its own scrim
  // and Escape handling, so this must not run there.
  useEffect(() => {
    if (!infoOpen || isMobile) return undefined;
    const onDown = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfoOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setInfoOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [infoOpen, isMobile]);

  // SMS balance + wallet. walletCredits is only present when the BE runs
  // poolAwareSmsDebit → we use its presence to show the Top-up affordance.
  const loadSmsBalance = (isAlive) => {
    dispatch(marketingAction.getSettings())
      .then((res) => {
        const r = res?.data?.responseData?.result;
        if ((!isAlive || isAlive()) && r) {
          // Prefer the pure free-pool numbers: remainingSms/marketingEmailSpendable
          // are SPENDABLE (free pool + wallet-affordable sends), so using them in
          // the credits roll-up would double-count wallet credits.
          if (r.marketingPoolRemaining !== undefined) setRemainingSms(r.marketingPoolRemaining);
          else if (r.remainingSms !== undefined && r.remainingSms !== null) setRemainingSms(r.remainingSms);
          if (r.walletCredits !== undefined) setWalletCredits(r.walletCredits);
          if (r.marketingEmailPoolRemaining !== undefined) setEmailSpendable(r.marketingEmailPoolRemaining);
          else if (r.marketingEmailSpendable !== undefined) setEmailSpendable(r.marketingEmailSpendable);
          if (r.remainingAutomatedSms !== undefined) setAutomatedSms(r.remainingAutomatedSms);
          if (r.freeCredits !== undefined) setFreeCredits(r.freeCredits);
          if (r.freeCreditsUsage) setFreeUsage(r.freeCreditsUsage);
          setUnifiedFree(!!r.unifiedFreeCredits);
          if (r.creditCosts) setCreditCosts(r.creditCosts);
          setWalletPence(walletBalancePence(r));
          setFreePence(r.freePence != null ? Number(r.freePence) : r.freeCredits != null ? creditsToPence(r.freeCredits, r.creditCosts && r.creditCosts.pencePerCredit) : null);
          setUnitPrices(messagePrices(r));
          // Whose pot these numbers describe. An SE covered by the salon (paidBy 1)
          // spends the OWNER's pot, so they see the balance but can't top it up —
          // a top-up credits the caller's own setting, which their sends never draw.
          if (r.canTopUpWallet !== undefined) setCanTopUp(!!r.canTopUpWallet);
          if (r.marketingPaidBy !== undefined) setSalonCoversMarketing(r.marketingPaidBy === 1);
          if (r.autoRecharge) setAutoRecharge(r.autoRecharge);
          setPoolAware(!!r.poolAware);
        }
      })
      .catch(() => {});
  };

  // Deep link from the Performance page: use the loaded rows when the
  // campaign is in them, otherwise fetch its details and synthesise a row —
  // under pagination an older campaign may not be on the first page.
  useEffect(() => {
    if (loading || !openId) return;
    const hit = rows.find((c) => c.id === openId);
    // A draft opens in its editor (Bailey's "Open in Campaigns"), the rest in
    // their details. Read-only while the salon runs this stylist's marketing.
    const show = (row) => {
      const kind = row.type === 'sms' ? 'sms' : 'email';
      if (opensInEditor(row, managed)) { (kind === 'sms' ? handleEditSms : handleEdit)(row); return; }
      setChannel(kind);
      setDetail({ kind, data: row });
    };
    if (hit) {
      show(hit);
    } else {
      marketingApiService.getCampaignDetails(dispatch, openId)
        .then((full) => {
          if (!full || !full._id) return;
          const row = {
            id: full._id,
            name: full.name,
            type: full.type,
            status: full.status,
            subject: full.subject,
            message: full.message,
            sentDate: full.sentDate,
            scheduledDate: full.scheduledDate,
            scheduledTime: full.scheduledTime,
            audience: full.audienceSegment,
            audienceSize: full.audienceSize,
            senderName: full.senderName,
            replyToEmail: full.replyToEmail,
            source: full.source || null,
            groupId: full.groupId || null,
            stats: full.status === 'sent' ? {
              sent: full.totalRecipients || 0,
              delivered: full.deliveredCount || 0,
              opened: full.openedCount || 0,
              clicked: full.clickedCount || 0,
              bookings: full.bookings || 0,
            } : null,
            _raw: full,
          };
          show(row);
        })
        .catch(() => {});
    }
    setOpenId(null);
    if (onOpenConsumed) onOpenConsumed();
  }, [loading, openId]); // eslint-disable-line react-hooks/exhaustive-deps

  const demoList = useMemo(
    () => (demoMode ? (channel === 'email' ? buildSampleEmails() : buildSampleSms()) : null),
    [demoMode, channel]
  );
  const list = demoMode ? demoList : rows;

  const counts = useMemo(() => {
    if (demoMode) {
      return {
        all: demoList.length,
        sent: demoList.filter((c) => c.status === 'sent').length,
        scheduled: demoList.filter((c) => c.status === 'scheduled').length,
        draft: demoList.filter((c) => c.status === 'draft').length,
      };
    }
    return {
      all: Object.values(statusCounts).reduce((a, b) => a + (b || 0), 0),
      sent: statusCounts.sent || 0,
      scheduled: statusCounts.scheduled || 0,
      draft: statusCounts.draft || 0,
    };
  }, [demoMode, demoList, statusCounts]);

  // Demo rows filter client-side; real rows arrive already filtered by the BE
  const filtered = useMemo(() => {
    if (!demoMode) return list;
    return list.filter((c) => {
      if (status !== 'all' && c.status !== status) return false;
      if (query) {
        const hay = `${c.name || ''} ${c.subject || ''} ${c.message || ''}`.toLowerCase();
        if (!hay.includes(query.toLowerCase())) return false;
      }
      return true;
    });
  }, [list, demoMode, status, query]);

  // Headline = the credit balance, nothing else. It previously added this
  // month's free allowances converted at each channel's rate, which inflated
  // the number and implied a fungible pot: free allowances are CHANNEL-LOCKED
  // (free SMS cannot pay for an email) and automations run on a different
  // pool entirely. The popover now lists the three separately instead.
  const creditSummary = useMemo(() => {
    const emailCost = Math.max(1, (creditCosts && creditCosts.email) || 1);
    const smsCost = Math.max(1, (creditCosts && creditCosts.sms) || 3);
    const freeEmails = Number(emailSpendable) || 0;
    const freeSms = Number(remainingSms) || 0;
    return {
      credits: Number(walletCredits) || 0,
      balancePence: walletPence != null ? walletPence : creditsToPence(walletCredits),
      freeAllowancePence: freePence,
      textPence: unitPrices ? unitPrices.textPence : smsCost * 2,
      emailPence: unitPrices ? unitPrices.emailPence : emailCost * 2,
      freeEmails,
      freeSms,
      // The free allowance in credits. Under the unified pool the BE hands us the
      // figure directly — deriving it as emails×1 + texts×3 would DOUBLE COUNT,
      // because those two are the same pool expressed in each channel's sends.
      // Only the legacy two-pool model can be summed.
      freeAsCredits: unifiedFree
        ? (Number(freeCredits) || 0)
        : (freeEmails * emailCost) + (freeSms * smsCost),
      unified: unifiedFree,
      usedEmails: (freeUsage && Number(freeUsage.emailSends)) || 0,
      usedSms: (freeUsage && Number(freeUsage.smsSends)) || 0,
      automatedSms: Number(automatedSms) || 0,
      emailCost,
      smsCost,
    };
  }, [walletCredits, walletPence, freePence, unitPrices, emailSpendable, remainingSms, automatedSms, creditCosts, freeCredits, freeUsage, unifiedFree]);

  /* Three separate pots, never added together: credits are fungible across
     marketing channels, the free marketing allowance is channel-locked, and
     automations run on their own pool that credits never touch. Shared by the
     web popover and the mobile sheet so the two cannot drift. */
  const creditBreakdown = (
    <>
      <div className="cmp-pop-title">Message balance</div>
      <div className="cmp-pop-row">
        <span>Balance</span>
        <strong>{formatPence(creditSummary.balancePence)}</strong>
      </div>
      <div className="cmp-pop-sub">
        Never expires · emails {formatUnitPence(creditSummary.emailPence)}, texts {formatUnitPence(creditSummary.textPence)}
      </div>

      <div className="cmp-pop-divider" />
      <div className="cmp-pop-title">Free this month</div>
      <div className="cmp-pop-row">
        <span>Allowance</span>
        <strong>{creditSummary.freeAllowancePence != null ? formatPence(creditSummary.freeAllowancePence) : formatPence(creditsToPence(creditSummary.freeAsCredits))}</strong>
      </div>
      {/* One pool, so the channels are alternatives — "or", never "and". Adding
          them would describe an allowance twice the size of the real one. */}
      <div className="cmp-pop-sub">
        {creditSummary.unified ? (
          <>
            Enough for {creditSummary.freeEmails.toLocaleString()} {creditSummary.freeEmails === 1 ? 'email' : 'emails'}
            {' or '}
            {creditSummary.freeSms.toLocaleString()} {creditSummary.freeSms === 1 ? 'text' : 'texts'}
          </>
        ) : (
          <>
            {creditSummary.freeEmails.toLocaleString()} marketing {creditSummary.freeEmails === 1 ? 'email' : 'emails'}
            {' · '}
            {creditSummary.freeSms.toLocaleString()} marketing {creditSummary.freeSms === 1 ? 'text' : 'texts'}
          </>
        )}
      </div>
      {creditSummary.unified && (creditSummary.usedEmails > 0 || creditSummary.usedSms > 0) && (
        <div className="cmp-pop-sub">
          Used so far: {creditSummary.usedEmails.toLocaleString()} {creditSummary.usedEmails === 1 ? 'email' : 'emails'}
          {' · '}
          {creditSummary.usedSms.toLocaleString()} {creditSummary.usedSms === 1 ? 'text' : 'texts'}
        </div>
      )}
      <div className="cmp-pop-sub">Used before your balance. Resets monthly.</div>

      <div className="cmp-pop-divider" />
      <div className="cmp-pop-title">Reminders &amp; automations</div>
      <div className="cmp-pop-row">
        <span>Free texts left</span>
        <strong>{creditSummary.automatedSms.toLocaleString()}</strong>
      </div>
      <p className="cmp-pop-note">
        A separate allowance. Reminders keep sending, even when your balance is empty.
        {salonCoversMarketing && ' The salon covers your marketing, so this is the salon’s shared balance.'}
      </p>
    </>
  );

  const switchChannel = (c) => { setChannel(c); setStatus('all'); setQuery(''); };
  const onNew = () => (channel === 'email' ? onCreateEmail() : onCreateSMS());

  // Already confirmed in the sheet (DuplicateDeleteRow). A failure throws with
  // a reason the sheet can show, rather than closing as if it had worked.
  const handleDelete = async (c) => {
    let res;
    try {
      res = await dispatch(marketingAction.deleteCampaign({ campaignId: c.id }));
    } catch (e) {
      const said = e && e.response && e.response.data && e.response.data.message;
      throw new Error(said || "Couldn't reach Beu. Check your connection and try again.");
    }
    const body = (res && res.data) || {};
    if (body.statusCode !== 1) {
      const said = (body.error && (body.error.responseMessage || body.error.errorMessage)) || body.message;
      throw new Error(said || "Couldn't delete this campaign. Please try again.");
    }
    loadCampaigns();
    // Stopped part way: keep the sheet open to say how many had already gone
    const done = (body.responseData && body.responseData.result) || {};
    if (done.stopped) return { stopped: true, message: body.responseData.message || 'Stopped. Nobody else will get it.' };
    setDetail(null);
    return null;
  };

  const handleEdit = (c) => {
    setDetail(null);
    if (onOpenCampaign) onOpenCampaign(c);
  };

  const handleEditSms = (c) => {
    setDetail(null);
    if (onOpenSMS) onOpenSMS(c);
  };

  const handleDuplicate = async (c) => {
    try {
      await marketingApiService.createCampaign(dispatch, {
        name: `${c.name || 'Campaign'} copy`,
        type: c.type || 'email',
        subject: c.subject,
        message: c.message,
        emailBlocks: c._raw?.emailBlocks || [],
        audienceSegment: c._raw?.audienceSegment || 'all',
        // Carry the hand-picked audience across, or a 'custom' copy lands with
        // no recipients.
        audienceFilters: c._raw?.audienceFilters || undefined,
        clientIds: c._raw?.audienceFilters?.clientIds || undefined,
        sendNow: false,
      });
    } catch (e) { /* surfaced by reload */ }
    setDetail(null);
    setStatus('draft');
    loadCampaigns();
  };

  const canBroadcast = multi && active && active.role === 'owner';

  // KPI strip: real data comes as all-time sent totals from the list
  // endpoint (accurate under pagination); demo mode sums the sample rows
  const emailTotals = useMemo(() => {
    if (!demoMode) {
      const t = serverTotals || {};
      return {
        campaigns: t.campaigns || 0,
        delivered: t.delivered || 0,
        openRate: pct(t.opened, t.delivered),
        clickRate: pct(t.clicked, t.delivered),
        bookings: t.bookings || 0,
      };
    }
    const sent = buildSampleEmails().filter((c) => c.status === 'sent' && c.stats);
    const sum = (k) => sent.reduce((n, c) => n + (Number(c.stats[k]) || 0), 0);
    const delivered = sum('delivered');
    return {
      campaigns: sent.length,
      delivered,
      openRate: pct(sum('opened'), delivered),
      clickRate: pct(sum('clicked'), delivered),
      bookings: sum('bookings'),
    };
  }, [demoMode, serverTotals]);

  /* ----- rows ----- */
  const EmailRow = ({ c }) => {
    const date = formatDate(c.sentDate || c.scheduledDate);
    const opens = pct(c.stats?.opened, c.stats?.delivered);
    const bookings = c.stats?.bookings;
    // Title on top, subject underneath; audience only fills in when there's
    // no subject (it lives in the detail pane header now)
    const audience = c.audience ? `${segmentLabel(c.audience)}${c.audienceSize ? ` (${Number(c.audienceSize).toLocaleString()})` : ''}` : '';
    const sub = c.subject || audience;
    if (isMobile) {
      return (
        <div className="cmp-card" onClick={() => setDetail({ kind: 'email', data: c })}>
          <div className="cmp-card-top">
            <CampaignThumb campaign={c} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="cmp-row-name">{c.name}</p>{partOf(c)}{fromBailey(c)}
              <p className="cmp-row-sub">{sub}</p>
            </div>
            <StatusPill status={c.status} heldReason={c.heldReason || (c._raw && c._raw.heldReason)} held={managed && isHeld(c)} />
          </div>
          <div className="cmp-card-meta">
            {date && <span>{date}</span>}
            {opens !== null && <span>{opens}% opens</span>}
            {bookings > 0 && <span className="pos">{bookings} bookings</span>}
          </div>
        </div>
      );
    }
    return (
      <div className="cmp-row" onClick={() => setDetail({ kind: 'email', data: c })}>
        <div className="cmp-row-main">
          <CampaignThumb campaign={c} />
          <div style={{ minWidth: 0 }}>
            <p className="cmp-row-name">{c.name}</p>{partOf(c)}{fromBailey(c)}
            <p className="cmp-row-sub">{sub}</p>
          </div>
        </div>
        <div className="cmp-status-wrap"><StatusPill status={c.status} heldReason={c.heldReason || (c._raw && c._raw.heldReason)} held={managed && isHeld(c)} /></div>
        <div className={`cmp-cell-date${date ? '' : ' cmp-cell-muted'}`}>{date || '·'}</div>
        <div className={`cmp-cell-r${opens === null ? ' cmp-cell-muted' : ''}`}>{opens === null ? '·' : `${opens}%`}</div>
        <div className={`cmp-cell-r${bookings ? ' cmp-cell-pos' : ' cmp-cell-muted'}`}>{bookings || '·'}</div>
      </div>
    );
  };

  const SmsRow = ({ c }) => {
    const date = formatDate(c.sentDate || c.scheduledDate);
    const delivered = c.stats?.delivered;
    const preview = (c.message || '').slice(0, isMobile ? 54 : 60);
    const audience = c.audience ? `${segmentLabel(c.audience)}${c.audienceSize ? ` (${Number(c.audienceSize).toLocaleString()})` : ''}` : '';
    if (isMobile) {
      return (
        <div className="cmp-card" onClick={() => setDetail({ kind: 'sms', data: c })}>
          <div className="cmp-card-top">
            <div className="cmp-thumb cmp-thumb--sms"><ChatIcon /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="cmp-row-name">{preview}{(c.message || '').length > 54 ? '…' : ''}</p>{partOf(c)}{fromBailey(c)}
              <p className="cmp-row-sub">{audience}</p>
            </div>
            <StatusPill status={c.status} heldReason={c.heldReason || (c._raw && c._raw.heldReason)} held={managed && isHeld(c)} />
          </div>
          <div className="cmp-card-meta">
            {date && <span>{date}</span>}
            {delivered ? <span className="pos">{Number(delivered).toLocaleString()} delivered</span> : null}
          </div>
        </div>
      );
    }
    return (
      <div className="cmp-row" onClick={() => setDetail({ kind: 'sms', data: c })}>
        <div className="cmp-row-main">
          <div className="cmp-thumb cmp-thumb--sms"><ChatIcon /></div>
          <div style={{ minWidth: 0 }}>
            <p className="cmp-row-name">{preview}{(c.message || '').length > 60 ? '…' : ''}</p>{partOf(c)}{fromBailey(c)}
            <p className="cmp-row-sub">{audience}</p>
          </div>
        </div>
        <div className="cmp-status-wrap"><StatusPill status={c.status} heldReason={c.heldReason || (c._raw && c._raw.heldReason)} held={managed && isHeld(c)} /></div>
        <div className={`cmp-cell-date${date ? '' : ' cmp-cell-muted'}`}>{date || '·'}</div>
        <div className={`cmp-cell-r${delivered ? ' cmp-cell-pos' : ' cmp-cell-muted'}`} style={{ gridColumn: 'span 2', textAlign: 'right' }}>
          {delivered ? Number(delivered).toLocaleString() : '·'}
        </div>
      </div>
    );
  };

  return (
    <div className="cmp-wrap">
      {/* Header */}
      <div className="cmp-head">
        <div className="cmp-head-text">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 className="cmp-title">Campaigns</h1>
            <button type="button" className="pv2-help-btn" onClick={() => setHelpOpen(true)} aria-label="How campaigns work" title="How campaigns work">?</button>
          </div>
          <p className="cmp-subtitle">{CAMPAIGNS_VISIBLE ? 'Posts, Stories, emails and texts for your clients, on their own or together as a campaign.' : 'Reach your clients with beautiful, on-brand email and SMS campaigns.'}</p>
        </div>
        <div className="cmp-head-actions">
          {/* Locations. Same control as the finance header, so "All locations"
              means the same thing in both places. Renders nothing for a
              single-site salon or for anyone who is not an owner or manager.
              The credits pill beside it is deliberately NOT scoped by this: a
              wallet belongs to a legal entity, not to whichever sites happen
              to be ticked, and showing a filtered balance next to a real one
              would invite exactly the wrong conclusion. */}
          {/* Pool-aware: show automated + marketing separately, with a persistent
              Top-up. Legacy (flag off): the single free-allowance readout.
              Neither when the salon runs this stylist's marketing: that
              balance is the salon's, not theirs to see or spend. */}
          {managed ? null : poolAware ? (
            <>
              {/* Buy sits inside the pill rather than floating beside it, so
                  the balance and the way to add to it read as one control. */}
              <div className={`cmp-allowance${canTopUp ? ' cmp-allowance--buy' : ''}`} ref={infoRef}>
                <div className="cmp-allowance-icon"><CreditIcon /></div>
                <div>
                  <div className="cmp-allowance-num">{formatPence(creditSummary.balancePence)}</div>
                  <div className="cmp-allowance-label">Balance</div>
                </div>
                <button
                  type="button"
                  className="cmp-allowance-info"
                  aria-label="What makes up this balance"
                  aria-expanded={infoOpen}
                  onClick={() => setInfoOpen((v) => !v)}
                >
                  i
                </button>
                {canTopUp && (
                  <button type="button" className="cmp-allowance-buy" onClick={() => setShowTopup(true)}>
                    Top up
                  </button>
                )}
                {/* Anchored popover on web; on mobile the same content opens
                    as the slide-up sheet the `?` walkthrough uses, since a
                    popover this tall is awkward to read on a phone. */}
                {infoOpen && !isMobile && (
                  <div className="cmp-allowance-pop" role="dialog" aria-label="Message balance breakdown">
                    {creditBreakdown}
                  </div>
                )}
              </div>
            </>
          ) : remainingSms !== null && (
            <div className="cmp-allowance" title="Free SMS allowance">
              <div className="cmp-allowance-icon"><AllowanceIcon /></div>
              <div>
                <div className="cmp-allowance-num">{Number(remainingSms).toLocaleString()}</div>
                <div className="cmp-allowance-label">SMS remaining</div>
              </div>
            </div>
          )}
        </div>
        {infoOpen && isMobile && (
          <InfoSheet eyebrow="Your balance" onClose={() => setInfoOpen(false)}>
            {creditBreakdown}
          </InfoSheet>
        )}
        {showTopup && (
          <SmsWalletTopupModal
            balancePence={creditSummary.balancePence}
            currentBalance={walletCredits}
            prices={unitPrices}
            creditCosts={creditCosts}
            autoRecharge={autoRecharge}
            onSuccess={() => loadSmsBalance()}
            onClose={() => setShowTopup(false)}
          />
        )}
      </div>

      {managed && <SalonRunsMarketingBanner salonName={managedBySalonName} />}

      {demoMode && <SampleDataBanner onExit={() => { setDemoMode(false); setDetail(null); }} />}

      {/* Toolbar — channel choice comes first, so the stats below it (which
          are scoped to whichever channel is picked) never read as unlabelled. */}
      <div className="cmp-toolbar">
        <div className="cmp-seg" role="tablist" aria-label="Channel">
          {CAMPAIGNS_VISIBLE && (
            <button className={`cmp-seg-btn${channel === 'campaigns' ? ' active' : ''}`} role="tab" aria-selected={channel === 'campaigns'} onClick={() => switchChannel('campaigns')}>
              <MegaphoneIcon />Campaigns
            </button>
          )}
          <button className={`cmp-seg-btn${channel === 'email' ? ' active' : ''}`} role="tab" aria-selected={channel === 'email'} onClick={() => switchChannel('email')}>
            <MailIcon />{CAMPAIGNS_VISIBLE ? 'Emails' : 'Email'}
          </button>
          <button className={`cmp-seg-btn${channel === 'sms' ? ' active' : ''}`} role="tab" aria-selected={channel === 'sms'} onClick={() => switchChannel('sms')}>
            <ChatIcon />{CAMPAIGNS_VISIBLE ? 'Texts' : 'SMS'}
          </button>
        </div>
        {channel !== 'campaigns' && <div className="cmp-search">
          <span className="cmp-search-icon"><SearchIcon /></span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={CAMPAIGNS_VISIBLE ? (channel === 'email' ? 'Search emails' : 'Search texts') : (channel === 'email' ? 'Search campaigns' : 'Search messages')}
            aria-label={CAMPAIGNS_VISIBLE ? (channel === 'email' ? 'Search emails' : 'Search texts') : 'Search campaigns'}
          />
        </div>}
      </div>

      {/* Performance at a glance — all-time totals across sent email
          campaigns, straight from the list endpoint's `totals`. */}
      {channel === 'email' && !isInitialLoad && emailTotals.campaigns > 0 && (
        <div className="cmp-kpis">
          <div className="cmp-kpi">
            <p className="cmp-kpi-label">{CAMPAIGNS_VISIBLE ? 'Emails sent' : 'Campaigns sent'}</p>
            {/* "Emails sent" counts emails; it showed the number of campaigns */}
            <p className="cmp-kpi-num">{(CAMPAIGNS_VISIBLE ? emailTotals.delivered : emailTotals.campaigns).toLocaleString('en-GB')}</p>
          </div>
          <div className="cmp-kpi">
            <p className="cmp-kpi-label">Open rate</p>
            <p className="cmp-kpi-num">{emailTotals.openRate !== null ? `${emailTotals.openRate}%` : '·'}</p>
          </div>
          <div className="cmp-kpi">
            <p className="cmp-kpi-label">Click rate</p>
            <p className="cmp-kpi-num">{emailTotals.clickRate !== null ? `${emailTotals.clickRate}%` : '·'}</p>
          </div>
          <div className="cmp-kpi">
            <p className="cmp-kpi-label">Bookings generated</p>
            <p className="cmp-kpi-num">{emailTotals.bookings}</p>
          </div>
        </div>
      )}

      {channel === 'campaigns' ? (
        <CampaignsList
          dispatch={dispatch}
          managed={managed}
          onOpenDraft={(row) => (row.type === 'sms' ? handleEditSms : handleEdit)(row)}
          openGroupId={openGroupId}
          startSignal={newCampaign}
          onOpenConsumed={onOpenConsumed}
        />
      ) : (<>
      {/* Status filter */}
      <div className="cmp-filter" role="tablist" aria-label="Status filter">
        {STATUS_TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={status === t.id} className={`cmp-filter-tab${status === t.id ? ' active' : ''}`} onClick={() => setStatus(t.id)}>
            {t.label}
            <span className="cmp-filter-count">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {!managed && !demoMode && !isInitialLoad && channel === 'email' && counts.sent === 0 && (
        <SampleDataPrompt onPreview={() => setDemoMode(true)} />
      )}

      {EMAIL_TEMPLATES_V2_VISIBLE && !demoMode && channel === 'email' && <BrandNudge />}

      {/* Quick-start ideas */}
      {/* With campaigns on, these shortcuts live on the new campaign page */}
      {channel === 'email' && !managed && !CAMPAIGNS_VISIBLE && (
        <div className="cmp-ideas">
          <span className="cmp-ideas-label">Need a nudge?</span>
          {/* Own element so mobile can scroll the chips in one row while the
              label sits above them, instead of wrapping to three rows. */}
          <div className="cmp-ideas-row">
            {[
              { id: 'gift-card-push', label: 'Sell gift cards' },
              { id: 'we-miss-you', label: 'Win back quiet clients' },
              { id: 'birthday-club', label: 'Birthday treats' },
              { id: 'flash-sale-pop', label: 'Fill quiet days' },
            ].map((idea) => (
              <button key={idea.id} type="button" className="cmp-idea-chip" onClick={() => onCreateEmail(idea.id)}>
                {idea.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* List — a status/search switch just dims the previous page in place
          (`loading` while `hasLoadedOnceRef` is already true) rather than
          swapping to the 5-row skeleton, which used to collapse the whole
          layout on every tab click. Only the true first load skeletons. */}
      {isInitialLoad ? (
        <SkeletonList />
      ) : (
        <div style={{ opacity: loading ? 0.45 : 1, transition: 'opacity 0.12s ease', pointerEvents: loading ? 'none' : 'auto' }}>
        {filtered.length === 0 ? (
          <div className="cmp-empty">
            <div className="cmp-empty-icon">{channel === 'email' ? <MegaphoneIcon /> : <ChatIcon />}</div>
            <h3 className="cmp-empty-title">
              {query || status !== 'all'
                ? 'Nothing matches that filter'
                : managed
                  ? (channel === 'email' ? (CAMPAIGNS_VISIBLE ? 'No emails of your own' : 'No campaigns of your own') : (CAMPAIGNS_VISIBLE ? 'No texts of your own' : 'No SMS blasts of your own'))
                  : channel === 'email' ? (CAMPAIGNS_VISIBLE ? 'Send your first email' : 'Send your first campaign') : (CAMPAIGNS_VISIBLE ? 'Send your first text' : 'Send your first SMS blast')}
            </h3>
            <p className="cmp-empty-text">
              {query || status !== 'all'
                ? 'Try a different search or status filter.'
                : managed
                  ? `${managedBySalonName || 'Your salon'} sends ${CAMPAIGNS_VISIBLE ? 'emails and texts' : 'campaigns'} to your clients for you.`
                  : channel === 'email'
                    ? 'Reach your whole client base with a beautiful, branded email in just a few minutes.'
                    : 'Send a short, personal message to your clients and watch the bookings come in.'}
            </p>
            {!(query || status !== 'all') && !managed && (
              <button className="cmp-new-btn" onClick={onNew}>
                <PlusIcon />{channel === 'email' ? (CAMPAIGNS_VISIBLE ? 'New email' : 'New campaign') : (CAMPAIGNS_VISIBLE ? 'New text' : 'New SMS blast')}
              </button>
            )}
          </div>
        ) : (
          <div className="cmp-list">
            {!isMobile && (
              <div className="cmp-thead">
                <div>{channel === 'email' ? (CAMPAIGNS_VISIBLE ? 'Email' : 'Campaign') : (CAMPAIGNS_VISIBLE ? 'Text' : 'Message')}</div>
                <div className="c">Status</div>
                <div className="r">Date</div>
                {channel === 'email' ? <><div className="r">Opens</div><div className="r">Bookings</div></> : <div className="r" style={{ gridColumn: 'span 2' }}>Delivered</div>}
              </div>
            )}
            {filtered.map((c) => (channel === 'email' ? <EmailRow key={c.id} c={c} /> : <SmsRow key={c.id} c={c} />))}
            {/* Append shimmer while the next page loads */}
            {!demoMode && loadingMore && [0, 1, 2].map((i) => (
              <div className="cmp-skel-row" key={`more-${i}`} aria-hidden="true">
                <div className="cmp-skel-main">
                  <div className="cmp-skel cmp-skel--thumb" />
                  <div className="cmp-skel-lines">
                    <div className="cmp-skel" style={{ height: 12, width: '55%' }} />
                    <div className="cmp-skel" style={{ height: 10, width: '35%' }} />
                  </div>
                </div>
                <div className="cmp-skel" style={{ height: 20, width: 78, justifySelf: 'center' }} />
                <div className="cmp-skel" style={{ height: 12, width: 64, justifySelf: 'end' }} />
                <div className="cmp-skel" style={{ height: 12, width: 36, justifySelf: 'end' }} />
                <div className="cmp-skel" style={{ height: 12, width: 36, justifySelf: 'end' }} />
              </div>
            ))}
            {/* Quiet end-cap once a long list is fully loaded */}
            {!demoMode && !hasMore && !loadingMore && filtered.length > PAGE_LIMIT && (
              <p style={{ textAlign: 'center', fontSize: 12, color: '#C7C7CC', padding: '14px 0 4px', margin: 0 }}>
                That's all {filtered.length} of them
              </p>
            )}
          </div>
        )}
        </div>
      )}

      </>)}

      {helpOpen && <CampaignsHelp onClose={() => setHelpOpen(false)} />}

      {broadcastCampaign && (
        <CampaignBroadcastModal
          campaign={broadcastCampaign}
          sites={sites}
          activeSalonId={activeSalonId}
          onClose={() => setBroadcastCampaign(null)}
        />
      )}

      {/* Detail panel */}
      {detail && (
        <SlidePanel title={detail.kind === 'email' ? (detail.data.status === 'sent' ? (CAMPAIGNS_VISIBLE ? 'Email results' : 'Campaign performance') : (CAMPAIGNS_VISIBLE ? 'Email details' : 'Campaign details')) : (CAMPAIGNS_VISIBLE ? 'Text details' : 'SMS details')} width={560} onClose={() => setDetail(null)}>
          {detail.kind === 'email'
            ? <EmailDetail c={detail.data} dispatch={dispatch} onDelete={handleDelete} onDuplicate={managed ? null : handleDuplicate} onEdit={managed ? null : handleEdit} onBroadcast={canBroadcast && !managed ? setBroadcastCampaign : null} heldBy={managed ? (managedBySalonName || 'Your salon') : null} />
            : <SmsDetail c={detail.data} dispatch={dispatch} onDelete={handleDelete} onDuplicate={managed ? null : handleDuplicate} onEdit={managed ? null : handleEditSms} onBroadcast={canBroadcast && !managed ? setBroadcastCampaign : null} heldBy={managed ? (managedBySalonName || 'Your salon') : null} />}
        </SlidePanel>
      )}
    </div>
  );
}

/* ---------- Detail content ---------- */
const Stat = ({ label, value, accent }) => (
  <div style={{ padding: '16px', background: accent ? 'rgba(0,126,160,0.08)' : '#fafafa', borderRadius: 12, border: '1px solid #f0f0f2' }}>
    <p style={{ fontSize: 12, color: accent ? '#007EA0' : '#8e8e93', margin: '0 0 4px', fontWeight: 500 }}>{label}</p>
    <p style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', color: accent ? '#007EA0' : '#29282D', margin: 0 }}>{value}</p>
  </div>
);

const FunnelRow = ({ label, value, base, color }) => {
  const width = base ? Math.max(2, Math.round((value / base) * 100)) : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '86px 1fr 84px', gap: 10, alignItems: 'center', marginBottom: 9 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#4B4B52' }}>{label}</span>
      <div style={{ height: 22, background: '#F2F2F7', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ width: `${width}%`, height: '100%', background: color, borderRadius: 6, transition: 'width 0.4s ease' }} />
      </div>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1A1A1E', textAlign: 'right' }}>
        {Number(value || 0).toLocaleString()}
        {base && value ? <span style={{ color: '#8E8E93', fontWeight: 500 }}> · {Math.round((value / base) * 100)}%</span> : null}
      </span>
    </div>
  );
};

const BenchmarkChip = ({ value, benchmark, label }) => {
  if (value === null || value === undefined) return null;
  const up = value >= benchmark;
  return (
    <span style={{
      display: 'inline-block', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.02em',
      color: up ? '#007EA0' : '#A05A1F', background: up ? 'rgba(0,126,160,0.1)' : 'rgba(160,90,31,0.1)',
      borderRadius: 100, padding: '3px 8px', marginTop: 6
    }}>
      {up ? 'Above' : 'Below'} {label}
    </span>
  );
};

/* Real block-level preview of the email, scaled to half size inside the
   detail panel. zoom (not transform) so the scroll height matches what is
   visible. Campaigns without blocks (SMS-era rows) render nothing. */
const EmailPreviewCard = ({ campaign }) => {
  const blocks = (campaign._raw && campaign._raw.emailBlocks) || campaign.emailBlocks;
  if (!Array.isArray(blocks) || !blocks.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: 16, marginBottom: 14 }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 12px' }}>Email preview</p>
      <div style={{ borderRadius: 10, border: '1px solid #F2F2F7', overflow: 'hidden auto', maxHeight: 420, background: '#fafafa' }}>
        <EmailBlocksPreview
          blocks={blocks}
          fallback={() => (
            <div style={{ zoom: 0.5, pointerEvents: 'none' }} aria-hidden>
              {blocks.map((block, idx) => (
                <BlockRenderer key={idx} block={{ ...block, id: `detail-prev-${idx}` }} />
              ))}
            </div>
          )}
        />
      </div>
    </div>
  );
};

// Duplicate and Delete, at the foot of a campaign's details. Delete asks first,
// right here in the sheet. It used window.confirm, which some embedded
// browsers never show (it just returns false), so Delete did nothing at all.
const pillBtn = { padding: '12px 22px', borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };
/* Why a draft or schedule will not go out while the salon runs marketing. */
/* salonName set: the salon runs it now. Unset: it was held while the salon
   ran it, and control has since come back, so it is an ordinary draft again. */
const HeldNote = ({ salonName }) => (
  <p style={{ margin: '0 0 12px', padding: '10px 12px', background: '#F7F7F9', borderRadius: 10, fontSize: 13, lineHeight: 1.5, color: '#4B4B52' }}>
    {salonName
      ? `Not sent. ${salonName} runs your marketing, so this stays on hold. You can still delete it.`
      : 'This was held back while the salon ran your marketing, so it has not been sent. Check it over before you send it.'}
  </p>
);

const DuplicateDeleteRow = ({ c, onDuplicate, onDelete }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [stoppedSaid, setStoppedSaid] = useState('');
  // While it's going out, deleting stops it: say so rather than "delete"
  const sending = c.status === 'sending';
  const cancel = () => { setConfirming(false); setError(''); };
  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await onDelete(c);
      if (res && res.stopped) { setStoppedSaid(res.message); setConfirming(false); setBusy(false); }
    } catch (e) {
      setError((e && e.message) || (sending ? "Couldn't stop this campaign. Please try again." : "Couldn't delete this campaign. Please try again."));
      setBusy(false);
    }
  };

  if (stoppedSaid) {
    return (
      <div role="status" style={{ border: '1px solid #DEDEE2', background: '#F7F7F9', borderRadius: 16, padding: 16 }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 650, color: '#29282D' }}>{stoppedSaid}</p>
      </div>
    );
  }

  if (confirming) {
    return (
      <div role="alertdialog" aria-labelledby="cmp-delete-q" style={{ border: '1px solid rgba(177,68,56,0.25)', background: '#FDF6F5', borderRadius: 16, padding: 16 }}>
        <p id="cmp-delete-q" style={{ margin: 0, fontSize: 15, fontWeight: 650, color: '#29282D' }}>{sending ? 'Stop sending this campaign?' : 'Delete this campaign?'}</p>
        <p style={{ margin: '4px 0 14px', fontSize: 13, color: '#6E6E73' }}>{sending ? 'Clients it has already reached will still have it. Nobody else will get it.' : "This can't be undone."}</p>
        {error && <p role="alert" style={{ margin: '0 0 14px', fontSize: 13, color: '#B14438' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={cancel} disabled={busy} style={{ ...pillBtn, border: '1px solid #DEDEE2', background: '#fff', color: '#29282D' }}>
            Cancel
          </button>
          <button type="button" onClick={remove} disabled={busy} style={{ ...pillBtn, border: 'none', background: '#B14438', color: '#fff', opacity: busy ? 0.6 : 1, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? (sending ? 'Stopping…' : 'Deleting…') : (sending ? 'Stop sending' : 'Delete')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 10 }}>
      {onDuplicate && (
      <button
        type="button"
        onClick={() => onDuplicate(c)}
        style={{ ...pillBtn, flex: 1, border: '1px solid #DEDEE2', background: '#fff', color: '#29282D' }}
      >
        Duplicate as draft
      </button>
      )}
      <button
        type="button"
        onClick={() => setConfirming(true)}
        style={{ ...pillBtn, border: '1px solid rgba(177,68,56,0.3)', background: '#fff', color: '#B14438' }}
      >
        {sending ? 'Stop sending' : 'Delete'}
      </button>
    </div>
  );
};

const EmailDetail = ({ c, dispatch, onDelete, onDuplicate, onEdit, onBroadcast, heldBy }) => {
  // Try the richer details endpoint; fall back to the list row's stats
  const [full, setFull] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');
  const [showAllActivity, setShowAllActivity] = useState(false);
  // Server-paged client activity (20 a page via campaign/recipients); demo
  // rows keep the legacy client-side filter/expand behaviour.
  const [paged, setPaged] = useState(null);
  useEffect(() => {
    let alive = true;
    if (c._demo) {
      // Sample rows carry their own detail payload and never touch the API
      setFull(c._demoDetail || null);
    } else if (c.status === 'sent') {
      marketingApiService.getCampaignDetails(dispatch, c.id)
        .then((res) => { if (alive && res) setFull(res); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [c.id, c.status, dispatch]);
  useEffect(() => {
    if (!c._demo && full && Array.isArray(full.recipients) && full.recipientsTotal !== undefined) {
      setPaged({ rows: full.recipients, total: full.recipientsTotal, hasMore: !!full.recipientsHasMore, page: 1, loading: false });
      setActivityFilter('all');
    }
  }, [full, c._demo]);

  const stats = useMemo(() => {
    if (full) {
      const sent = full.totalRecipients ?? full.sent ?? c.stats?.sent;
      const delivered = full.deliveredCount ?? full.delivered ?? c.stats?.delivered;
      const opened = full.openedCount ?? full.opened ?? c.stats?.opened;
      const clicked = full.clickedCount ?? full.clicked ?? c.stats?.clicked;
      const bookings = full.bookingCount ?? full.bookings ?? c.stats?.bookings;
      if (sent || delivered) return { sent, delivered, opened, clicked, bookings };
    }
    return c.stats;
  }, [full, c.stats]);

  const open = pct(stats?.opened, stats?.delivered);
  const click = pct(stats?.clicked, stats?.delivered);
  const ctor = pct(stats?.clicked, stats?.opened);

  // Phorest-style attribution transparency: the 14-day click window means
  // bookings keep attributing for 14 days (336h) after sending
  const hoursSinceSend = c.sentDate ? (Date.now() - new Date(c.sentDate).getTime()) / 36e5 : null;
  const stillMeasuring = hoursSinceSend !== null && hoursSinceSend >= 0 && hoursSinceSend < 336;

  const recipients = full?.recipients || full?.recipientActivity || null;
  const links = full?.links || full?.topLinks || null;
  const timeline = full?.timeline || null;
  const bounced = full?.bouncedCount ?? full?.bounced ?? c.stats?.bounced ?? null;
  const unsubscribed = full?.unsubscribedCount ?? full?.unsubscribed ?? c.stats?.unsubscribed ?? null;
  const revenue = full?.revenue ?? full?.attributedRevenue ?? null;

  const serverPaged = !c._demo && !!paged;

  const fetchActivityPage = (filter, page, append) => {
    setPaged((p) => ({ ...(p || { rows: [], total: 0, hasMore: false, page: 1 }), loading: true }));
    marketingApiService.getCampaignRecipients(dispatch, { campaignId: c.id, page, limit: 20, activity: filter })
      .then((res) => {
        setPaged((p) => ({
          rows: append ? [...((p && p.rows) || []), ...res.recipients] : res.recipients,
          total: res.totalCount,
          hasMore: res.hasMore,
          page,
          loading: false,
        }));
      })
      .catch(() => setPaged((p) => (p ? { ...p, loading: false } : p)));
  };

  const selectActivityFilter = (id) => {
    setActivityFilter(id);
    setShowAllActivity(false);
    if (serverPaged) fetchActivityPage(id, 1, false);
  };

  // Legacy (demo) path: filter + expand entirely client-side
  const filteredRecipients = Array.isArray(recipients)
    ? recipients.filter((r) => {
      if (activityFilter === 'all') return true;
      const st = (r.status || '').toLowerCase();
      if (activityFilter === 'booked') return st === 'booked';
      if (activityFilter === 'clicked') return st === 'clicked' || st === 'booked';
      if (activityFilter === 'opened') return st === 'opened' || st === 'clicked' || st === 'booked';
      return true;
    })
    : [];
  const visibleRecipients = showAllActivity ? filteredRecipients : filteredRecipients.slice(0, 6);
  const activityRows = serverPaged ? ((paged && paged.rows) || []) : visibleRecipients;
  const activityTotalAll = serverPaged ? (full?.recipientsTotal || 0) : (Array.isArray(recipients) ? recipients.length : 0);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px', color: '#29282D', margin: '0 0 6px' }}>{c.name}</h3>
        {c.subject && <p style={{ fontSize: 14, color: '#6e6e73', margin: 0 }}>{c.subject}</p>}
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <StatusPill status={c.status} heldReason={c.heldReason || (c._raw && c._raw.heldReason)} held={!!heldBy} />
          {formatDate(c.sentDate || c.scheduledDate) && (
            <span style={{ fontSize: 12.5, color: '#8e8e93' }}>
              {c.status === 'scheduled' ? 'Sends ' : ''}{formatDate(c.sentDate || c.scheduledDate)}
            </span>
          )}
          {c.audience && (
            <span style={{ fontSize: 12.5, color: '#8e8e93' }}>
              · {segmentLabel(c.audience)}{c.audienceSize ? ` (${Number(c.audienceSize).toLocaleString()})` : ''}
            </span>
          )}
        </div>
      </div>

      {stats ? (
        <>
          {stillMeasuring && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px',
              background: 'rgba(0,126,160,0.07)', borderRadius: 10, marginBottom: 18
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#007EA0', flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#007EA0' }}>
                Still measuring · bookings attribute for 14 days after sending
              </span>
            </div>
          )}

          {/* Funnel */}
          <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: '18px 16px', marginBottom: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 14px' }}>
              How it performed
            </p>
            <FunnelRow label="Sent" value={stats.sent} base={stats.sent} color="#29282D" />
            <FunnelRow label="Delivered" value={stats.delivered} base={stats.sent} color="#4B4B52" />
            <FunnelRow label="Opened" value={stats.opened} base={stats.sent} color="#007EA0" />
            <FunnelRow label="Clicked" value={stats.clicked} base={stats.sent} color="#00ACC9" />
            <FunnelRow label="Booked" value={stats.bookings} base={stats.sent} color="#005F79" />
            {(bounced !== null || unsubscribed !== null) && (
              <p style={{ fontSize: 12, color: '#8E8E93', margin: '10px 0 0', fontWeight: 500 }}>
                {bounced !== null ? `${Number(bounced).toLocaleString()} bounced` : ''}
                {bounced !== null && unsubscribed !== null ? ' · ' : ''}
                {unsubscribed !== null ? `${Number(unsubscribed).toLocaleString()} unsubscribed` : ''}
              </p>
            )}
          </div>

          {/* Rates with salon benchmarks */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
            <div style={{ padding: '13px 14px', background: '#fafafa', borderRadius: 12, border: '1px solid #f0f0f2' }}>
              <p style={{ fontSize: 11.5, color: '#8e8e93', margin: '0 0 3px', fontWeight: 600 }}>
                Open rate
                <span
                  title="Apple Mail privately preloads emails, which can inflate open rates. Clicks and bookings are the more reliable signal."
                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 13, height: 13, borderRadius: '50%', background: '#E5E5EA', color: '#6e6e73', fontSize: 9, fontWeight: 700, marginLeft: 5, cursor: 'help', verticalAlign: 'middle' }}
                >i</span>
              </p>
              <p style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.5px', color: '#29282D', margin: 0 }}>{open !== null ? `${open}%` : '·'}</p>
              <BenchmarkChip value={open} benchmark={21} label="salon avg (21%)" />
            </div>
            <div style={{ padding: '13px 14px', background: '#fafafa', borderRadius: 12, border: '1px solid #f0f0f2' }}>
              <p style={{ fontSize: 11.5, color: '#8e8e93', margin: '0 0 3px', fontWeight: 600 }}>Click rate</p>
              <p style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.5px', color: '#29282D', margin: 0 }}>{click !== null ? `${click}%` : '·'}</p>
              <BenchmarkChip value={click} benchmark={1} label="salon avg (1%)" />
            </div>
            <div style={{ padding: '13px 14px', background: '#fafafa', borderRadius: 12, border: '1px solid #f0f0f2' }}>
              <p style={{ fontSize: 11.5, color: '#8e8e93', margin: '0 0 3px', fontWeight: 600 }}>Clicks per open</p>
              <p style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.5px', color: '#29282D', margin: 0 }}>{ctor !== null ? `${ctor}%` : '·'}</p>
            </div>
          </div>

          {/* Bookings call-out */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', background: 'rgba(0,126,160,0.07)', borderRadius: 12, marginBottom: 14
          }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#007EA0', margin: '0 0 2px' }}>Bookings generated</p>
              <p style={{ fontSize: 11.5, color: '#4B7C8C', margin: 0 }}>Bookings made within 14 days of a click</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', color: '#007EA0', margin: 0 }}>{stats.bookings || 0}</p>
              {revenue !== null && (
                <p style={{ fontSize: 12.5, fontWeight: 700, color: '#007EA0', margin: '2px 0 0' }}>£{Number(revenue).toLocaleString()} revenue</p>
              )}
            </div>
          </div>

          {/* Engagement over the first 72 hours, when tracking provides it */}
          {Array.isArray(timeline) && timeline.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: 16, marginBottom: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 12px' }}>Engagement after sending</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 86 }}>
                {timeline.map((t, i) => {
                  const max = Math.max(...timeline.map((x) => x.opens || 0), 1);
                  const oh = Math.max(4, Math.round(((t.opens || 0) / max) * 70));
                  const chs = Math.max(2, Math.round(((t.clicks || 0) / max) * 70));
                  return (
                    <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 70 }}>
                        <div style={{ width: 12, height: oh, background: '#007EA0', borderRadius: 3 }} title={`${t.opens || 0} opens`} />
                        <div style={{ width: 12, height: chs, background: '#00ACC9', borderRadius: 3, opacity: 0.75 }} title={`${t.clicks || 0} clicks`} />
                      </div>
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: '#8E8E93', whiteSpace: 'nowrap' }}>{t.label}</span>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: '#8E8E93', margin: '10px 0 0' }}>Dark bars are opens, light bars are clicks.</p>
            </div>
          )}

          {/* Top links — renders when the details endpoint provides them */}
          {Array.isArray(links) && links.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: '16px', marginBottom: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 10px' }}>Top links</p>
              {links.slice(0, 5).map((l, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderTop: i ? '1px solid #F2F2F7' : 'none' }}>
                  <span style={{ fontSize: 12.5, color: '#4B4B52', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.url || l.link}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: '#1A1A1E', flexShrink: 0 }}>{Number(l.clicks || l.count || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recipient activity — filterable, 20 a page */}
          {(serverPaged ? activityTotalAll > 0 : Array.isArray(recipients) && recipients.length > 0) ? (
            <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: '16px', marginBottom: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 10px' }}>Client activity</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {[
                  { id: 'all', label: `All (${Number(activityTotalAll).toLocaleString()})` },
                  { id: 'opened', label: 'Opened' },
                  { id: 'clicked', label: 'Clicked' },
                  { id: 'booked', label: 'Booked' },
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => selectActivityFilter(f.id)}
                    style={{
                      padding: '6px 12px', borderRadius: 100, fontSize: 12, fontWeight: 600,
                      border: activityFilter === f.id ? 'none' : '1px solid #ECECF0',
                      background: activityFilter === f.id ? '#29282D' : '#fff',
                      color: activityFilter === f.id ? '#fff' : '#4B4B52',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {(serverPaged ? !paged.loading && activityRows.length === 0 : filteredRecipients.length === 0) && (
                <p style={{ fontSize: 12.5, color: '#8E8E93', margin: '10px 0 2px' }}>No clients with that activity.</p>
              )}
              {activityRows.map((r, i) => {
                const status = (r.status || (r.clicked ? 'clicked' : r.opened ? 'opened' : 'delivered')).toLowerCase();
                const isBooked = status === 'booked';
                const isSkipped = status === 'skipped';
                return (
                  <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid #F2F2F7' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 12.5, color: '#1A1A1E', fontWeight: 600 }}>{r.name || r.email}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'capitalize', borderRadius: 100, padding: '3px 9px',
                        color: (isBooked || status === 'clicked') ? '#007EA0' : isSkipped ? '#A05A1F' : '#8E8E93',
                        background: (isBooked || status === 'clicked') ? 'rgba(0,126,160,0.1)' : isSkipped ? 'rgba(160,90,31,0.1)' : '#F2F2F7'
                      }}>
                        {status}
                      </span>
                    </div>
                    {isBooked && (r.services || r.spend !== undefined) && (
                      <p style={{ fontSize: 12, color: '#4B7C8C', margin: '4px 0 0' }}>
                        {r.services ? r.services : ''}
                        {r.services && r.spend !== undefined ? ' · ' : ''}
                        {r.spend !== undefined ? `£${Number(r.spend).toLocaleString()} spent` : ''}
                      </p>
                    )}
                    {isSkipped && (
                      <p style={{ fontSize: 12, color: '#A05A1F', margin: '4px 0 0' }}>{skipLabel(r.skipReason)} · not billed</p>
                    )}
                  </div>
                );
              })}
              {serverPaged ? (
                (paged.hasMore || paged.loading) && (
                  <button
                    type="button"
                    disabled={paged.loading}
                    onClick={() => fetchActivityPage(activityFilter, paged.page + 1, true)}
                    style={{
                      width: '100%', marginTop: 10, padding: '9px', border: '1px solid #ECECF0',
                      borderRadius: 8, background: '#fff', fontSize: 12.5, fontWeight: 600,
                      color: '#4B4B52', cursor: paged.loading ? 'default' : 'pointer', fontFamily: 'inherit',
                      opacity: paged.loading ? 0.6 : 1
                    }}
                  >
                    {paged.loading ? 'Loading…' : `Show 20 more (${activityRows.length} of ${Number(paged.total).toLocaleString()})`}
                  </button>
                )
              ) : (
                filteredRecipients.length > 6 && (
                  <button
                    type="button"
                    onClick={() => setShowAllActivity((v) => !v)}
                    style={{
                      width: '100%', marginTop: 10, padding: '9px', border: '1px solid #ECECF0',
                      borderRadius: 8, background: '#fff', fontSize: 12.5, fontWeight: 600,
                      color: '#4B4B52', cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    {showAllActivity ? 'Show fewer' : `Show all ${filteredRecipients.length}`}
                  </button>
                )
              )}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: '#8e8e93', lineHeight: 1.5, margin: '0 0 14px' }}>
              Per-client activity and link tracking arrive with the upgraded sending engine.
            </p>
          )}
        </>
      ) : (
        <p style={{ fontSize: 14, color: '#8e8e93', lineHeight: 1.55, margin: '0 0 14px' }}>
          {c.status === 'scheduled'
            ? 'Results will appear here once this campaign has been sent.'
            : "This campaign hasn't been sent yet, so there are no results to show."}
        </p>
      )}

      {/* What the email actually looks like — fills the panel out for
          drafts/scheduled, and closes the loop for sent campaigns */}
      <EmailPreviewCard campaign={c} />

      {/* Actions (hidden for sample rows) — app-standard pill buttons */}
      {c._demo ? null : (
      <div style={{ marginTop: 6, paddingTop: 16, borderTop: '1px solid #F2F2F7' }}>
        {c.status !== 'sent' && (heldBy || isHeld(c)) && <HeldNote salonName={heldBy} />}
        {/* A draft/scheduled campaign can be reopened in the composer */}
        {(c.status === 'draft' || c.status === 'scheduled') && onEdit && (
          <button
            type="button"
            className="beu-btn-pill"
            onClick={() => onEdit(c)}
            style={{ width: '100%', marginBottom: 10 }}
          >
            {c.status === 'scheduled' ? (CAMPAIGNS_VISIBLE ? 'Edit email' : 'Edit campaign') : 'Continue editing'}
          </button>
        )}
        {onBroadcast && (
          <button type="button" onClick={() => onBroadcast(c)} style={{ width: '100%', marginBottom: 10, padding: '12px 22px', border: '1px solid #B9DDE6', borderRadius: 999, background: '#F2FBFD', fontSize: 14, fontWeight: 650, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}>
            Copy to other locations
          </button>
        )}
        <DuplicateDeleteRow c={c} onDuplicate={onDuplicate} onDelete={onDelete} />
      </div>
      )}
    </div>
  );
};

const SmsDetail = ({ c, dispatch, onDelete, onDuplicate, onEdit, onBroadcast, heldBy }) => {
  // Richer detail from the details endpoint when it responds; sample rows
  // carry their own payload
  const [full, setFull] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  // Server-paged activity (20 a page) — same model as EmailDetail, no filters
  const [paged, setPaged] = useState(null);
  useEffect(() => {
    let alive = true;
    if (c._demo) {
      setFull(c._demoDetail || null);
    } else if (c.status === 'sent') {
      marketingApiService.getCampaignDetails(dispatch, c.id)
        .then((res) => { if (alive && res) setFull(res); })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [c.id, c.status, dispatch]);
  useEffect(() => {
    if (!c._demo && full && Array.isArray(full.recipients) && full.recipientsTotal !== undefined) {
      setPaged({ rows: full.recipients, total: full.recipientsTotal, hasMore: !!full.recipientsHasMore, page: 1, loading: false });
    }
  }, [full, c._demo]);

  const stats = useMemo(() => {
    if (full) {
      const sent = full.totalRecipients ?? full.sent ?? c.stats?.sent;
      const delivered = full.deliveredCount ?? full.delivered ?? c.stats?.delivered;
      const clicked = full.clickedCount ?? full.clicked ?? c.stats?.clicked;
      const bookings = full.bookingCount ?? full.bookings ?? c.stats?.bookings;
      if (sent || delivered) return { sent, delivered, clicked, bookings };
    }
    return c.stats;
  }, [full, c.stats]);

  const hoursSinceSend = c.sentDate ? (Date.now() - new Date(c.sentDate).getTime()) / 36e5 : null;
  const stillMeasuring = hoursSinceSend !== null && hoursSinceSend >= 0 && hoursSinceSend < 336;
  const recipients = full?.recipients || full?.recipientActivity || null;
  const serverPaged = !c._demo && !!paged;
  const loadMoreActivity = () => {
    setPaged((p) => (p ? { ...p, loading: true } : p));
    marketingApiService.getCampaignRecipients(dispatch, { campaignId: c.id, page: (paged?.page || 1) + 1, limit: 20, activity: 'all' })
      .then((res) => {
        setPaged((p) => ({
          rows: [...((p && p.rows) || []), ...res.recipients],
          total: res.totalCount,
          hasMore: res.hasMore,
          page: res.page,
          loading: false,
        }));
      })
      .catch(() => setPaged((p) => (p ? { ...p, loading: false } : p)));
  };
  const visibleRecipients = serverPaged
    ? ((paged && paged.rows) || [])
    : Array.isArray(recipients)
      ? (showAllActivity ? recipients : recipients.slice(0, 6))
      : [];

  return (
    <div>
      <div style={{ background: '#29282D', borderRadius: 20, padding: 16, marginBottom: 16 }}>
        <div style={{ background: '#007EA0', color: '#fff', padding: '12px 16px', borderRadius: '16px 16px 4px 16px', fontSize: 14, lineHeight: 1.5 }}>
          {c.message}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatusPill status={c.status} heldReason={c.heldReason || (c._raw && c._raw.heldReason)} held={!!heldBy} />
        {formatDate(c.sentDate || c.scheduledDate) && (
          <span style={{ fontSize: 12.5, color: '#8e8e93' }}>
            {c.status === 'scheduled' ? 'Sends ' : ''}{formatDate(c.sentDate || c.scheduledDate)}
          </span>
        )}
        {c.audience && (
          <span style={{ fontSize: 12.5, color: '#8e8e93' }}>
            · {segmentLabel(c.audience)}{c.audienceSize ? ` (${Number(c.audienceSize).toLocaleString()})` : ''}
          </span>
        )}
      </div>

      {stats ? (
        <>
          {stillMeasuring && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, padding: '10px 14px',
              background: 'rgba(0,126,160,0.07)', borderRadius: 10, marginBottom: 16
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#007EA0', flexShrink: 0 }} />
              <span style={{ fontSize: 12.5, fontWeight: 600, color: '#007EA0' }}>
                Still measuring · bookings attribute for 14 days after sending
              </span>
            </div>
          )}

          <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: '18px 16px', marginBottom: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 14px' }}>
              How it performed
            </p>
            <FunnelRow label="Sent" value={stats.sent} base={stats.sent} color="#29282D" />
            <FunnelRow label="Delivered" value={stats.delivered} base={stats.sent} color="#007EA0" />
            {stats.clicked !== undefined && stats.clicked !== null && (
              <FunnelRow label="Tapped link" value={stats.clicked} base={stats.sent} color="#00ACC9" />
            )}
            {stats.bookings !== undefined && stats.bookings !== null && (
              <FunnelRow label="Booked" value={stats.bookings} base={stats.sent} color="#005F79" />
            )}
            <p style={{ fontSize: 11.5, color: '#8E8E93', margin: '10px 0 0', lineHeight: 1.5 }}>
              Texts do not report opens. Booked counts bookings made within 14 days of delivery.
            </p>
          </div>

          {Array.isArray(recipients) && recipients.length > 0 ? (
            <div style={{ background: '#fff', border: '1px solid #ECECF0', borderRadius: 14, padding: '16px', marginBottom: 14 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 10px' }}>Client activity</p>
              {visibleRecipients.map((r, i) => {
                const status = (r.status || (r.clicked ? 'clicked' : 'delivered')).toLowerCase();
                const isPositive = status === 'booked' || status === 'clicked';
                const isSkipped = status === 'skipped';
                return (
                  <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid #F2F2F7' : 'none' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 12.5, color: '#1A1A1E', fontWeight: 600 }}>{r.name || r.phone}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, textTransform: 'capitalize', borderRadius: 100, padding: '3px 9px',
                        color: isPositive ? '#007EA0' : status === 'failed' ? '#B14438' : isSkipped ? '#A05A1F' : '#8E8E93',
                        background: isPositive ? 'rgba(0,126,160,0.1)' : status === 'failed' ? 'rgba(177,68,56,0.08)' : isSkipped ? 'rgba(160,90,31,0.1)' : '#F2F2F7'
                      }}>
                        {status}
                      </span>
                    </div>
                    {isSkipped && (
                      <p style={{ fontSize: 12, color: '#A05A1F', margin: '4px 0 0' }}>{skipLabel(r.skipReason)} · not billed</p>
                    )}
                  </div>
                );
              })}
              {serverPaged ? (
                (paged.hasMore || paged.loading) && (
                  <button
                    type="button"
                    disabled={paged.loading}
                    onClick={loadMoreActivity}
                    style={{ width: '100%', marginTop: 10, padding: '9px', border: '1px solid #ECECF0', borderRadius: 8, background: '#fff', fontSize: 12.5, fontWeight: 600, color: '#4B4B52', cursor: paged.loading ? 'default' : 'pointer', fontFamily: 'inherit', opacity: paged.loading ? 0.6 : 1 }}
                  >
                    {paged.loading ? 'Loading…' : `Show 20 more (${visibleRecipients.length} of ${Number(paged.total).toLocaleString()})`}
                  </button>
                )
              ) : (
                recipients.length > 6 && (
                  <button
                    type="button"
                    onClick={() => setShowAllActivity((v) => !v)}
                    style={{ width: '100%', marginTop: 10, padding: '9px', border: '1px solid #ECECF0', borderRadius: 8, background: '#fff', fontSize: 12.5, fontWeight: 600, color: '#4B4B52', cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    {showAllActivity ? 'Show fewer' : `Show all ${recipients.length}`}
                  </button>
                )
              )}
            </div>
          ) : (
            <p style={{ fontSize: 12, color: '#8e8e93', lineHeight: 1.5, margin: '0 0 14px' }}>
              Per-client delivery and link tracking arrive with the upgraded sending engine.
            </p>
          )}
        </>
      ) : (
        <p style={{ fontSize: 14, color: '#8e8e93', lineHeight: 1.55 }}>
          {c.status === 'scheduled'
            ? (CAMPAIGNS_VISIBLE ? 'Results will appear here once this text has been sent.' : 'Results will appear here once this blast has been sent.')
            : (CAMPAIGNS_VISIBLE ? "This text hasn't been sent yet, so there are no results to show." : "This blast hasn't been sent yet, so there are no results to show.")}
        </p>
      )}

      {c._demo ? null : (
        <div style={{ marginTop: 6, paddingTop: 16, borderTop: '1px solid #F2F2F7' }}>
        {c.status !== 'sent' && (heldBy || isHeld(c)) && <HeldNote salonName={heldBy} />}
        {/* A draft/scheduled blast can be reopened in the SMS composer */}
        {(c.status === 'draft' || c.status === 'scheduled') && onEdit && (
          <button
            type="button"
            className="beu-btn-pill"
            onClick={() => onEdit(c)}
            style={{ width: '100%', marginBottom: 10 }}
          >
            {c.status === 'scheduled' ? (CAMPAIGNS_VISIBLE ? 'Edit text' : 'Edit blast') : 'Continue editing'}
          </button>
        )}
        {onBroadcast && (
          <button type="button" onClick={() => onBroadcast(c)} style={{ width: '100%', marginBottom: 10, padding: '12px 22px', border: '1px solid #B9DDE6', borderRadius: 999, background: '#F2FBFD', fontSize: 14, fontWeight: 650, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}>
            Copy to other locations
          </button>
        )}
        <DuplicateDeleteRow c={c} onDuplicate={onDuplicate} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
};

export const CampaignsHub = connect()(CampaignsHubComponent);
export default CampaignsHub;
